import clsx from 'clsx';
import React, { ComponentPropsWithoutRef, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { ClickAnchor, ClickAnchorProps } from '../../ClickMenu';
import { Icon } from '../../Icon';
import { elementsInputClasses, InputContainer, InputContainerProps } from '../Input';
import { DropdownMenu, DropdownOption } from './components';
import { DropdownContextProvider } from './dropdown.context';
import { IDropdownOption } from './types';

export interface DropdownProps
  extends Omit<InputContainerProps, 'isReadOnly' | 'children' | 'trailing'>,
    Pick<
      ClickAnchorProps,
      'placement' | 'popperJSHideModifierOptions' | 'popperJSPreventOverflowOptions' | 'popperJSFlipModifierOptions'
    > {
  name?: string;
  value?: string | null;
  placeholder?: string;
  children?: React.ReactNode;
  withClearSelection?: boolean;
  retriggerSelectionFromValue?: boolean;
  onChange?: (value: IDropdownOption['value'], option: IDropdownOption | null) => void;
  dropdownMenuClassName?: HTMLDivElement['className'];
  onBlur?: ComponentPropsWithoutRef<'input'>['onBlur'];
  loading?: boolean;
  renderDisplayValue?: () => string | undefined;
}

export const Dropdown = forwardRef<HTMLInputElement, DropdownProps>(
  (
    {
      name,
      onBlur,
      value,
      status,
      onChange,
      children,
      placement,
      placeholder,
      withClearSelection,
      dropdownMenuClassName,
      retriggerSelectionFromValue,
      popperJSHideModifierOptions,
      popperJSPreventOverflowOptions,
      popperJSFlipModifierOptions,
      loading,
      renderDisplayValue,
      ...inputContainerProps
    },
    ref
  ) => {
    const [selected, setSelected] = useState<IDropdownOption | null>(null);

    // DropdownOption (a user click) is the only path that reaches `setSelected` through the
    // context; the value->selected effect below writes state directly. A ref — not state — so
    // marking intent adds no render, keeping the ELE-850 loop surface unchanged.
    const selectedByUser = useRef(false);
    const selectByUser = useCallback<React.Dispatch<React.SetStateAction<IDropdownOption | null>>>((next) => {
      selectedByUser.current = true;
      setSelected(next);
    }, []);

    useEffect(() => {
      if (!Array.isArray(children)) return;

      const option = children?.find((child: any) => (child as JSX.Element).props.value === value) as JSX.Element;

      // support for deselecting
      setSelected(option?.props || null);
    }, [value, retriggerSelectionFromValue]);

    useEffect(() => {
      const nextValue = selected ? selected.value : null;
      // `selected` is set both by user clicks (DropdownOption) and by the value->selected sync
      // effect above. Only a user action makes `selected` diverge from the incoming `value`; a
      // pure sync leaves them equal. Echoing that synced value back through onChange re-fires the
      // parent's handler on every mount/remount — and for the controlled react-hook-form
      // contribution fields that echo is an onChange(null) that writes null back into the form,
      // which the controlled `values` restores, spinning into an infinite render loop (ELE-850).
      // Emit only when the selection actually changed relative to `value`.
      if ((nextValue ?? null) === (value ?? null)) return;

      // On mount `selected` is still null while `value` is already set, and the sync effect's
      // state update lands a render later — so this effect sees a divergence that is not a user
      // action. Emitting there sends onChange(null), which InvestmentIncomeSourceDropdown accepts
      // once the income sources have resolved — which a warm React Query cache makes true on the
      // very first render — wiping incomeSourceID, percentage, amount and frequency off a saved
      // investment (ELE-668).
      if (!selectedByUser.current) return;
      selectedByUser.current = false;

      onChange?.(nextValue, selected);
    }, [selected]);

    return (
      <DropdownContextProvider selected={selected} setSelected={selectByUser}>
        <ClickAnchor
          disabled={status === 'disabled'}
          offset={[0, 5]}
          inPortal={false}
          placement={placement}
          popperJSFlipModifierOptions={popperJSFlipModifierOptions}
          popperJSHideModifierOptions={popperJSHideModifierOptions}
          popperJSPreventOverflowOptions={popperJSPreventOverflowOptions}
          floatingElement={
            children ? (
              <DropdownMenu className={clsx(dropdownMenuClassName, '!max-w-[calc(100vw_-_48px)]')}>
                {withClearSelection && (
                  <DropdownOption value={null} label="Clear Selection" id="Dropdown_clear_selection" />
                )}
                {children}
              </DropdownMenu>
            ) : (
              <></>
            )
          }
        >
          <InputContainer
            isReadOnly
            status={status}
            trailing={
              <Icon
                icon={loading ? 'spinner' : 'arrowDropdown'}
                className="ElementsInput-icon !h-[24px] !w-[24px] text-palette-600"
              />
            }
            {...inputContainerProps}
          >
            {selected && value && (
              <input
                readOnly
                id={name}
                ref={ref}
                name={name}
                className="hidden"
                value={value ?? undefined}
                disabled={status === 'disabled'}
              />
            )}
            <input
              readOnly
              onBlur={onBlur}
              placeholder={placeholder}
              value={renderDisplayValue?.() || selected?.label || ''}
              disabled={status === 'disabled'}
              className={clsx(elementsInputClasses.input, 'cursor-pointer')}
            />
          </InputContainer>
        </ClickAnchor>
      </DropdownContextProvider>
    );
  }
);
